import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { IconCloudDownload, IconTrash } from '@rossum/ui/icons/tabler';
import { IconButton, Stack, SvgIcon, Tooltip } from '@rossum/ui/material';
import { ViewerRestrictor } from '../../../components/Restrictors';
import { linebreak } from '../../../lib/formaterValues';
import { isDeletable } from '../../../redux/modules/annotation/helpers';
import { openModal } from '../../../redux/modules/modal/actions';
import { hasFailedProcessing } from '../../../types/attachmentStatus';
import { useGuardedDownloadOriginalDocuments } from '../../document/hooks/useGuardedDownloadOriginalDocuments';
import { RestrictedAccessIcon } from '../../pricing/components/RestrictedAccessIcon';
import { isImageOrPdf } from '../helpers';
import { AttachmentT } from './EmailAttachment/helpers';

type EmailAttachmentActionsProps = {
  attachment: AttachmentT;
  onDelete: () => void;
};

export const EmailAttachmentActions = ({
  attachment,
  onDelete,
}: EmailAttachmentActionsProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const downloadOriginalDocuments = useGuardedDownloadOriginalDocuments();

  const { mimeType, originalFileName, content, attachmentStatus } =
    attachment.document;

  // A failed-processing warning takes precedence over the unrecognised-format
  // one, so let the guarded download own the confirmation in that case.
  const onDownload = () =>
    hasFailedProcessing(attachmentStatus) || isImageOrPdf(mimeType)
      ? downloadOriginalDocuments([attachment.document])
      : dispatch(
          openModal({
            textId: 'unsafeAttachmentDownload',
            onConfirm: () => {
              downloadOriginalDocuments([attachment.document]);
            },
          })
        );

  return (
    <ViewerRestrictor>
      <Stack direction="row" spacing={1} alignItems="center">
        {originalFileName !== null && content && (
          <Tooltip
            title={intl.formatMessage({
              id: 'components.emailDocumentsPreview.downloadTooltip',
            })}
            placement="top"
          >
            <span>
              <IconButton
                aria-label="download"
                data-cy="email-card-download-original-file"
                onClick={onDownload}
                disabled={Boolean(attachment.annotation?.restrictedAccess)}
              >
                <SvgIcon fontSize="medium">
                  <IconCloudDownload />
                </SvgIcon>
              </IconButton>
            </span>
          </Tooltip>
        )}
        {attachment.annotation && isDeletable(attachment.annotation.status) && (
          <Tooltip
            placement="top"
            title={
              intl.formatMessage(
                { id: 'components.emailDocumentsPreview.deleteTooltip' },
                {
                  linebreak,
                }
              ) ?? ''
            }
            sx={{ textAlign: 'center' }}
          >
            <IconButton aria-label="delete" onClick={onDelete}>
              <SvgIcon fontSize="medium">
                <IconTrash />
              </SvgIcon>
            </IconButton>
          </Tooltip>
        )}
        {attachment.annotation?.restrictedAccess ? (
          <RestrictedAccessIcon placement="top" />
        ) : null}
      </Stack>
    </ViewerRestrictor>
  );
};
